import { Link } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import GrowCard from "../components/GrowCard.jsx";
import { useGrowsQuery } from "../hooks/useGrowsQuery.js";
import { useQuery } from "@tanstack/react-query";

const API = import.meta.env.VITE_API_BASE_URL;

function useDashboardQuery() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export default function Grows() {
  const { data: grows, isLoading, isError } = useGrowsQuery();
  const { data: dashboard } = useDashboardQuery();

  if (isLoading) return <LayoutWide title="Grows"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Grows"><div className="p-4 text-red-600">Failed to load grows.</div></LayoutWide>;

  const active = (grows ?? []).filter(g => g.status === "active");
  const completed = (grows ?? []).filter(g => g.status !== "active");

  function liveDeviceFor(grow) {
    if (!grow.device_id || !dashboard?.devices) return null;
    return dashboard.devices[grow.device_id] ?? null;
  }

  return (
    <LayoutWide title="Grows">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-cyan-800">Grows</h1>
        <Link
          to="/grows/new"
          className="text-sm bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 transition-colors font-medium"
        >
          + New Grow
        </Link>
      </div>

      {active.length === 0 && completed.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <div className="text-5xl mb-4">🍄</div>
          <div className="text-lg font-medium text-gray-500 mb-2">No grows yet</div>
          <div className="text-sm mb-6">Start tracking your first cultivation batch.</div>
          <Link
            to="/grows/new"
            className="text-sm bg-cyan-600 text-white rounded px-4 py-2 hover:bg-cyan-700 transition-colors font-medium"
          >
            Start your first grow
          </Link>
        </div>
      )}

      {active.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Active ({active.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {active.map(grow => (
              <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
            ))}
          </div>
        </section>
      )}

      {completed.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Completed ({completed.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {completed.map(grow => (
              <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
            ))}
          </div>
        </section>
      )}
    </LayoutWide>
  );
}
